import React, { useEffect, useRef, useState, useCallback } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Form, Button, Badge } from "react-bootstrap";
import { Lock, Unlock, Compass, ZoomIn, ZoomOut, Upload, Globe, Layers, RotateCcw, RotateCw, Map as MapIcon, Search, X } from "lucide-react";
import Axios, { authHeaders, getProxiedImageUrl } from "../../utils/axios";
import { useToast } from "../../context/TostContext";
import CustomSelect from "../../components/form/CustomSelect";
import { CheckboxOption, MultiValueMessage } from "../../components/form/utils";

const getRotatedCursor = (baseDir, rotation) => {
  const angleDeg = (rotation * 180) / Math.PI;
  const normAngle = ((angleDeg % 360) + 360) % 360;
  const index = Math.floor(((normAngle + 22.5) % 180) / 45);

  if (baseDir === "w") {
    const cursors = ["ew-resize", "nesw-resize", "ns-resize", "nwse-resize"];
    return cursors[index];
  } else if (baseDir === "h") {
    const cursors = ["ns-resize", "nwse-resize", "ew-resize", "nesw-resize"];
    return cursors[index];
  } else if (baseDir === "nwse") {
    const cursors = ["nwse-resize", "ns-resize", "nesw-resize", "ew-resize"];
    return cursors[index];
  } else if (baseDir === "nesw") {
    const cursors = ["nesw-resize", "ew-resize", "nwse-resize", "ns-resize"];
    return cursors[index];
  }
  return "pointer";
};

export default function VisualMapOverlayEditor({
  imageUrl,
  initialCorners,
  onCornersChange,
  onFileUpload,
  isLocked: externalLocked = false,
  activeFileId,
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const fileInputRef = useRef(null);
  const currentUrlRef = useRef(imageUrl);
  const isDraggingActiveRef = useRef(false);
  const toast = useToast();

  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [mapStyle, setMapStyle] = useState("streets"); // 'streets' | 'satellite'
  const [opacity, setOpacity] = useState(0.75);
  const [isLocked, setIsLocked] = useState(externalLocked);
  const [aspectRatio, setAspectRatio] = useState(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const dragTargetMode = "overlay"; // 'overlay' | 'map'
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);

  // Listen to Ctrl / Cmd key to dynamically allow map panning
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Control" || e.key === "Meta") setIsCtrlPressed(true);
    };
    const handleKeyUp = (e) => {
      if (e.key === "Control" || e.key === "Meta") setIsCtrlPressed(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  // Nearby completed maps guides
  const [showNearbyCompleted, setShowNearbyCompleted] = useState(false);
  const [nearbyMaps, setNearbyMaps] = useState([]);
  const [mapTypes, setMapTypes] = useState([]);
  const [selectedMapTypes, setSelectedMapTypes] = useState([]);

  // Core visual placement state
  const [visualState, setVisualState] = useState({
    center: [72.5099, 23.04],
    scaleX: 1.0,
    scaleY: 1.0,
    rotation: 0.0,
  });

  const [baseMercWidth, setBaseMercWidth] = useState(0.005);
  const [baseMercHeight, setBaseMercHeight] = useState(0.005);
  const [screenPoints, setScreenPoints] = useState([]);
  const [corners, setCorners] = useState([]);

  const isDefaultSelectedRef = useRef(false);

  // Load map types on mount and default to active map sheet's type (e.g. TP)
  useEffect(() => {
    const fetchMapTypesList = async () => {
      try {
        const res = await Axios.get("/maps/map-types/all?is_active=true&is_map_georeference=true", authHeaders());
        const list = res.data?.data || [];
        setMapTypes(list);

        if (!isDefaultSelectedRef.current) {
          let targetTypeId = null;
          if (activeFileId) {
            try {
              const fileRes = await Axios.get(`/maps/map-files/georeference/list?id=${activeFileId}`, authHeaders());
              const fileRecord = fileRes.data?.data?.data?.find((f) => String(f.id) === String(activeFileId)) || fileRes.data?.data?.data?.[0];
              targetTypeId = fileRecord?.map_type_id || fileRecord?.mapType?.id;
            } catch (err) {}
          }

          const matched = list.find((t) => targetTypeId && String(t.id) === String(targetTypeId));
          if (matched) {
            setSelectedMapTypes([{ value: matched.id, label: matched.name }]);
            isDefaultSelectedRef.current = true;
          } else if (list.length > 0) {
            setSelectedMapTypes([{ value: list[0].id, label: list[0].name }]);
            isDefaultSelectedRef.current = true;
          } else {
            setSelectedMapTypes([]);
          }
        }
      } catch (err) {
        console.error("Failed to load map types:", err);
      }
    };
    fetchMapTypesList();
  }, [activeFileId]);

  const nearbySourcesRef = useRef(new Set());
  const nearbyLayersRef = useRef(new Set());

  // Purge nearby maps state when selected map types filter changes
  useEffect(() => {
    setNearbyMaps([]);
  }, [selectedMapTypes]);

  // Load nearby completed map sheets with BBOX spatial viewport querying
  useEffect(() => {
    if (!showNearbyCompleted) {
      setNearbyMaps([]);
      return;
    }

    const fetchNearby = async () => {
      if (mapTypes.length > 0 && (selectedMapTypes || []).length === 0) {
        setNearbyMaps([]);
        return;
      }

      try {
        const mapTypeIds = (selectedMapTypes || []).map((t) => (typeof t === "object" ? t.value || t.id : t)).filter(Boolean).join(",");
        let url = "/maps/map-files/georeference/nearby?is_georeferenced=true";
        if (mapTypeIds) {
          url += `&map_type_id=${encodeURIComponent(mapTypeIds)}`;
        }
        if (activeFileId) {
          url += `&exclude_id=${encodeURIComponent(activeFileId)}`;
        }

        const map = mapRef.current;
        if (map && typeof map.getBounds === "function") {
          const bounds = map.getBounds();
          const sw = bounds.getSouthWest();
          const ne = bounds.getNorthEast();
          const lngPad = Math.abs(ne.lng - sw.lng) * 0.15;
          const latPad = Math.abs(ne.lat - sw.lat) * 0.15;
          const bbox = `${(sw.lng - lngPad).toFixed(6)},${(sw.lat - latPad).toFixed(6)},${(ne.lng + lngPad).toFixed(6)},${(ne.lat + latPad).toFixed(6)}`;
          url += `&bbox=${encodeURIComponent(bbox)}`;
        }

        const res = await Axios.get(url, authHeaders());
        const list = res.data?.data?.data || [];

        // Filter state to keep only maps matching current selectedMapTypes
        setNearbyMaps((prev) => {
          const selectedTypeIds = (selectedMapTypes || []).map((t) => (typeof t === "object" ? String(t.value || t.id) : String(t))).filter(Boolean);
          const selectedSet = new Set(selectedTypeIds);

          const validPrev = (prev || []).filter((item) => {
            if (selectedSet.size === 0) return true;
            return selectedSet.has(String(item.map_type_id));
          });

          const mapDict = new Map(validPrev.map((item) => [item.id, item]));
          list.forEach((item) => {
            mapDict.set(item.id, item);
          });
          return Array.from(mapDict.values());
        });
      } catch (err) {
        console.error("Failed to load nearby completed map sheets:", err);
      }
    };

    fetchNearby();

    const map = mapRef.current;
    if (!map) return;

    let timer = null;
    const handleMoveEnd = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        fetchNearby();
      }, 200);
    };

    map.on("moveend", handleMoveEnd);
    return () => {
      if (timer) clearTimeout(timer);
      map.off("moveend", handleMoveEnd);
    };
  }, [activeFileId, selectedMapTypes, mapTypes.length, showNearbyCompleted, isMapLoaded]);


  // Calculate 4 LngLat corners from Mercator coordinates & rotation angle
  const calculateCorners = useCallback(
    (center, scaleX, scaleY, rotation, bWidth, bHeight) => {
      if (!center || !center[0] || !center[1]) return [];
      const centerMerc = maplibregl.MercatorCoordinate.fromLngLat(center);
      const w = bWidth * scaleX;
      const h = bHeight * scaleY;

      // 4 Unrotated offsets (TL, TR, BR, BL)
      const offsets = [
        [-w / 2, -h / 2],
        [w / 2, -h / 2],
        [w / 2, h / 2],
        [-w / 2, h / 2],
      ];

      const cos = Math.cos(rotation);
      const sin = Math.sin(rotation);

      return offsets.map(([dx, dy]) => {
        const rx = dx * cos - dy * sin;
        const ry = dx * sin + dy * cos;
        const x = centerMerc.x + rx;
        const y = centerMerc.y + ry;
        const coord = new maplibregl.MercatorCoordinate(x, y);
        const lngLat = coord.toLngLat();
        return [lngLat.lng, lngLat.lat];
      });
    },
    []
  );

  // Initialize MapLibre GL
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
      center: [72.5099, 23.04],
      zoom: 12,
      dragRotate: false,
      doubleClickZoom: false,
      boxZoom: false,
    });

    map.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      "top-right"
    );

    map.on("load", () => {
      map.resize();
      setIsMapLoaded(true);
      setTimeout(() => {
        if (mapRef.current) mapRef.current.resize();
      }, 200);
      setTimeout(() => {
        if (mapRef.current) mapRef.current.resize();
      }, 600);
    });

    mapRef.current = map;

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  const onCornersChangeRef = useRef(onCornersChange);
  useEffect(() => {
    onCornersChangeRef.current = onCornersChange;
  }, [onCornersChange]);

  const hasInitializedRef = useRef(false);
  const hasFittedInitialBoundsRef = useRef(false);

  useEffect(() => {
    hasInitializedRef.current = false;
    hasFittedInitialBoundsRef.current = false;
  }, [imageUrl]);

  // Automatically fit camera bounds ONCE on initial page load if initialCorners exist
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded || hasFittedInitialBoundsRef.current) return;
    if (!initialCorners || !Array.isArray(initialCorners) || initialCorners.length !== 4) return;

    const lngs = initialCorners.map((c) => c[0]);
    const lats = initialCorners.map((c) => c[1]);

    try {
      map.fitBounds(
        [
          [Math.min(...lngs), Math.min(...lats)],
          [Math.max(...lngs), Math.max(...lats)],
        ],
        { padding: 80, animate: false }
      );
      hasFittedInitialBoundsRef.current = true;
    } catch (err) {
      console.warn("Auto fitBounds initial load error:", err);
    }
  }, [isMapLoaded, initialCorners]);

  const fittedFileIdRef = useRef("");

  const isInternalUpdateRef = useRef(false);

  // Re-sync visualState & fit camera bounds ONCE per map sheet
  useEffect(() => {
    if (!initialCorners || !Array.isArray(initialCorners) || initialCorners.length !== 4) return;
    
    // Ignore internal echo updates produced by VisualMapOverlayEditor's own handle dragging
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }
    
    const isNewFile = fittedFileIdRef.current !== activeFileId;

    try {
      const tlMerc = maplibregl.MercatorCoordinate.fromLngLat(initialCorners[0]);
      const trMerc = maplibregl.MercatorCoordinate.fromLngLat(initialCorners[1]);
      const brMerc = maplibregl.MercatorCoordinate.fromLngLat(initialCorners[2]);
      const blMerc = maplibregl.MercatorCoordinate.fromLngLat(initialCorners[3]);

      const w = Math.hypot(trMerc.x - tlMerc.x, trMerc.y - tlMerc.y) || 0.005;
      const h = Math.hypot(blMerc.x - tlMerc.x, blMerc.y - tlMerc.y) || 0.005;
      const calcAspectRatio = (w / h) || aspectRatio || 1.33;

      const centerMercX = (tlMerc.x + trMerc.x + brMerc.x + blMerc.x) / 4;
      const centerMercY = (tlMerc.y + trMerc.y + brMerc.y + blMerc.y) / 4;
      const centerLngLat = new maplibregl.MercatorCoordinate(centerMercX, centerMercY).toLngLat();

      const rotation = Math.atan2(trMerc.y - tlMerc.y, trMerc.x - tlMerc.x);

      setBaseMercWidth(w);
      setBaseMercHeight(w / calcAspectRatio);
      setCorners(initialCorners);
      setVisualState({ center: [centerLngLat.lng, centerLngLat.lat], scaleX: 1.0, scaleY: 1.0, rotation });

      const map = mapRef.current;
      if (map && isMapLoaded && isNewFile) {
        const lngs = initialCorners.map((c) => c[0]);
        const lats = initialCorners.map((c) => c[1]);
        map.fitBounds(
          [
            [Math.min(...lngs), Math.min(...lats)],
            [Math.max(...lngs), Math.max(...lats)],
          ],
          { padding: 80, animate: false }
        );
        fittedFileIdRef.current = activeFileId;
      }
    } catch (err) {
      console.warn("Error re-syncing initialCorners in VisualMapOverlayEditor:", err);
    }
  }, [initialCorners, isMapLoaded, activeFileId, aspectRatio]);

  // Load image aspect ratio with fallback (runs once per imageUrl)
  useEffect(() => {
    if (hasInitializedRef.current) return;

    const applyAspectRatio = (ar) => {
      setAspectRatio(ar);

      const map = mapRef.current;
      const initialCenter = map ? [map.getCenter().lng, map.getCenter().lat] : [72.5099, 23.04];

      if (initialCorners && Array.isArray(initialCorners) && initialCorners.length === 4) {
        const tlMerc = maplibregl.MercatorCoordinate.fromLngLat(initialCorners[0]);
        const trMerc = maplibregl.MercatorCoordinate.fromLngLat(initialCorners[1]);
        const brMerc = maplibregl.MercatorCoordinate.fromLngLat(initialCorners[2]);
        const blMerc = maplibregl.MercatorCoordinate.fromLngLat(initialCorners[3]);

        const w = Math.hypot(trMerc.x - tlMerc.x, trMerc.y - tlMerc.y);
        const h = Math.hypot(blMerc.x - tlMerc.x, blMerc.y - tlMerc.y);

        const centerMercX = (tlMerc.x + trMerc.x + brMerc.x + blMerc.x) / 4;
        const centerMercY = (tlMerc.y + trMerc.y + brMerc.y + blMerc.y) / 4;
        const centerLngLat = new maplibregl.MercatorCoordinate(centerMercX, centerMercY).toLngLat();

        const rotation = Math.atan2(trMerc.y - tlMerc.y, trMerc.x - tlMerc.x);

        setBaseMercWidth(w || 0.005);
        setBaseMercHeight(h || (0.005 / ar));
        setCorners(initialCorners);
        setVisualState({ center: [centerLngLat.lng, centerLngLat.lat], scaleX: 1.0, scaleY: 1.0, rotation });

        if (map) {
          const lngs = initialCorners.map((c) => c[0]);
          const lats = initialCorners.map((c) => c[1]);
          map.fitBounds(
            [
              [Math.min(...lngs), Math.min(...lats)],
              [Math.max(...lngs), Math.max(...lats)],
            ],
            { padding: 80, animate: false }
          );
        }
      } else {
        const zoom = map ? map.getZoom() : 12;
        const initialWidth = (1 / Math.pow(2, zoom)) * 0.35;
        const initialHeight = initialWidth / ar;

        setBaseMercWidth(initialWidth);
        setBaseMercHeight(initialHeight);

        const calculated = calculateCorners(
          initialCenter,
          1.0,
          1.0,
          0.0,
          initialWidth,
          initialHeight
        );
        setVisualState({ center: initialCenter, scaleX: 1.0, scaleY: 1.0, rotation: 0.0 });
        setCorners(calculated);
      }
      hasInitializedRef.current = true;
    };

    if (!imageUrl) {
      applyAspectRatio(1.33);
      return;
    }

    const img = new Image();
    img.onload = () => {
      const ar = img.naturalWidth / img.naturalHeight || 1.33;
      applyAspectRatio(ar);
    };
    img.onerror = () => {
      console.warn("Failed to load image for aspect ratio, using default ratio 1.33");
      applyAspectRatio(1.33);
    };
    img.src = imageUrl;
  }, [imageUrl, initialCorners, calculateCorners]);

  // Recalculate corners when visual state or dimensions change
  useEffect(() => {
    if (!aspectRatio) return;
    const calc = calculateCorners(
      visualState.center,
      visualState.scaleX,
      visualState.scaleY,
      visualState.rotation,
      baseMercWidth,
      baseMercHeight
    );

    setCorners((prev) => {
      if (
        prev &&
        prev.length === 4 &&
        prev.every(
          (pt, i) =>
            Math.abs(pt[0] - calc[i][0]) < 1e-7 &&
            Math.abs(pt[1] - calc[i][1]) < 1e-7
        )
      ) {
        return prev;
      }
      return calc;
    });

    if (onCornersChangeRef.current) {
      isInternalUpdateRef.current = true;
      onCornersChangeRef.current(calc, visualState);
    }
  }, [visualState, baseMercWidth, baseMercHeight, aspectRatio, calculateCorners]);

  // Sync image source with MapLibre GL
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded || !imageUrl || corners.length !== 4) return;

    const sourceId = "visual-overlay-source";
    const layerId = "visual-overlay-layer";
    const proxiedUrl = getProxiedImageUrl(imageUrl);

    try {
      const existingSource = map.getSource(sourceId);

      if (existingSource) {
        // If image URL changed (e.g. user uploaded a new image), recreate source & layer
        if (currentUrlRef.current !== imageUrl) {
          if (map.getLayer(layerId)) map.removeLayer(layerId);
          if (map.getSource(sourceId)) map.removeSource(sourceId);

          map.addSource(sourceId, {
            type: "image",
            url: proxiedUrl,
            coordinates: corners,
          });

          map.addLayer({
            id: layerId,
            type: "raster",
            source: sourceId,
            paint: {
              "raster-opacity": Number(opacity),
              "raster-fade-duration": 0,
            },
          });
          currentUrlRef.current = imageUrl;
        } else {
          existingSource.setCoordinates(corners);
        }
      } else {
        map.addSource(sourceId, {
          type: "image",
          url: proxiedUrl,
          coordinates: corners,
        });

        map.addLayer({
          id: layerId,
          type: "raster",
          source: sourceId,
          paint: {
            "raster-opacity": Number(opacity),
            "raster-fade-duration": 0,
          },
        });
        currentUrlRef.current = imageUrl;
      }
    } catch (err) {
      console.warn("MapLibre overlay sync waiting for style load:", err.message);
    }
  }, [isMapLoaded, imageUrl, corners, opacity]);

  // Update opacity dynamically for both active and nearby guides
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    try {
      if (map.getLayer("visual-overlay-layer")) {
        map.setPaintProperty("visual-overlay-layer", "raster-opacity", Number(opacity));
      }

      // Sync nearby layers opacity
      if (nearbyMaps && nearbyMaps.length > 0) {
        nearbyMaps.forEach((item) => {
          const layerId = `nearby-layer-${item.id}`;
          if (map.getLayer(layerId)) {
            map.setPaintProperty(layerId, "raster-opacity", Number(opacity));
          }
        });
      }
    } catch (err) {
      console.warn("Error setting opacity property:", err);
    }
  }, [opacity, nearbyMaps]);

  // Handle satellite basemap layer switching
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded || !map.isStyleLoaded()) return;

    const satSourceId = "satellite-tile-source";
    const satLayerId = "satellite-tile-layer";

    try {
      if (!map.getSource(satSourceId)) {
        map.addSource(satSourceId, {
          type: "raster",
          tiles: [
            "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          ],
          tileSize: 256,
          maxzoom: 19,
          attribution: "Esri, Maxar, Earthstar Geographics",
        });
      }

      if (!map.getLayer(satLayerId)) {
        const beforeLayer = map.getLayer("visual-overlay-layer") ? "visual-overlay-layer" : undefined;
        map.addLayer(
          {
            id: satLayerId,
            type: "raster",
            source: satSourceId,
            layout: {
              visibility: mapStyle === "satellite" ? "visible" : "none",
            },
          },
          beforeLayer
        );
      } else {
        map.setLayoutProperty(
          satLayerId,
          "visibility",
          mapStyle === "satellite" ? "visible" : "none"
        );
      }
    } catch (err) {
      console.warn("MapLibre satellite layer waiting for style load:", err.message);
    }
  }, [mapStyle, isMapLoaded]);

  // Render nearby completed map sheets as Guides with Incremental Layer Diffing (Zero-Blink)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded) return;

    const cleanupNearbyLayers = () => {
      nearbyLayersRef.current.forEach((layerId) => {
        try {
          if (map.getLayer(layerId)) map.removeLayer(layerId);
        } catch (e) {
          console.warn("Failed to remove layer:", layerId, e);
        }
      });
      nearbyLayersRef.current.clear();

      nearbySourcesRef.current.forEach((sourceId) => {
        try {
          if (map.getSource(sourceId)) map.removeSource(sourceId);
        } catch (e) {
          console.warn("Failed to remove source:", sourceId, e);
        }
      });
      nearbySourcesRef.current.clear();
    };

    if (!showNearbyCompleted) {
      cleanupNearbyLayers();
      return;
    }

    // Purge any active Mapbox layers whose map item is no longer inside nearbyMaps
    const activeNearbyMapIds = new Set((nearbyMaps || []).map((m) => String(m.id)));
    const layersToPurge = [];
    nearbyLayersRef.current.forEach((layerId) => {
      const mapId = layerId.replace("nearby-layer-", "");
      if (!activeNearbyMapIds.has(mapId)) {
        layersToPurge.push(layerId);
      }
    });

    layersToPurge.forEach((layerId) => {
      const sourceId = layerId.replace("nearby-layer-", "nearby-source-");
      try {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
      } catch (e) {}
      nearbyLayersRef.current.delete(layerId);

      try {
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      } catch (e) {}
      nearbySourcesRef.current.delete(sourceId);
    });

    if (nearbyMaps.length > 0) {
      nearbyMaps.forEach((item) => {
        // Skip current sheet being edited
        const isCurrent =
          String(item.id) === String(activeFileId) ||
          item.file_url === imageUrl ||
          (item.georeference && item.georeference.georef_original_url === imageUrl);
        if (isCurrent) return;

        const georef = item.georeference;
        const cornersData = georef?.corners;
        if (!cornersData || !Array.isArray(cornersData) || cornersData.length !== 4) return;

        const sourceId = `nearby-source-${item.id}`;
        const layerId = `nearby-layer-${item.id}`;

        // If source & layer already exist, leave them untouched to prevent flickering
        if (map.getSource(sourceId) && map.getLayer(layerId)) return;

        const lngs = cornersData.map((c) => c[0]);
        const lats = cornersData.map((c) => c[1]);
        const flatBounds = [
          Math.min(...lngs),
          Math.min(...lats),
          Math.max(...lngs),
          Math.max(...lats),
        ];

        const tiledFileUrl = georef?.tiled_file_url;
        const warpedFileUrl = georef?.warped_file_url;

        try {
          if (!map.getSource(sourceId)) {
            if (tiledFileUrl) {
              const cleanTiledFileUrl = tiledFileUrl.split("?")[0];
              const baseUrl = cleanTiledFileUrl.split("/{z}")[0];
              const extension = cleanTiledFileUrl.substring(cleanTiledFileUrl.lastIndexOf("."));
              const proxiedTileUrl = `${getProxiedImageUrl(baseUrl)}/{z}/{x}/{y}${extension}`;

              map.addSource(sourceId, {
                type: "raster",
                tiles: [proxiedTileUrl],
                tileSize: 256,
                bounds: flatBounds,
                minzoom: 0,
                maxzoom: 22,
              });
            } else if (warpedFileUrl) {
              const uprightCorners = [
                [flatBounds[0], flatBounds[3]], // Top-Left
                [flatBounds[2], flatBounds[3]], // Top-Right
                [flatBounds[2], flatBounds[1]], // Bottom-Right
                [flatBounds[0], flatBounds[1]], // Bottom-Left
              ];
              map.addSource(sourceId, {
                type: "image",
                url: getProxiedImageUrl(warpedFileUrl),
                coordinates: uprightCorners,
              });
            } else {
              const rawUrl = item.file_url || georef?.georef_original_url;
              map.addSource(sourceId, {
                type: "image",
                url: getProxiedImageUrl(rawUrl),
                coordinates: cornersData,
              });
            }
            nearbySourcesRef.current.add(sourceId);
          }

          if (!map.getLayer(layerId)) {
            const beforeLayer = map.getLayer("visual-overlay-layer")
              ? "visual-overlay-layer"
              : undefined;
            map.addLayer(
              {
                id: layerId,
                type: "raster",
                source: sourceId,
                paint: {
                  "raster-opacity": Number(opacity),
                  "raster-fade-duration": 300,
                },
              },
              beforeLayer
            );
            nearbyLayersRef.current.add(layerId);
          }
        } catch (err) {
          console.warn("Error rendering nearby completed map overlay:", err);
        }
      });
    }
  }, [isMapLoaded, showNearbyCompleted, nearbyMaps, imageUrl, mapStyle, activeFileId, opacity]);

  // Project corners to screen points for SVG handle rendering
  const updateScreenPoints = useCallback(() => {
    const map = mapRef.current;
    if (!map || corners.length !== 4) return;

    const pts = corners.map((c) => map.project(c));
    setScreenPoints(pts);
  }, [corners]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded) return;

    updateScreenPoints();

    map.on("move", updateScreenPoints);
    map.on("zoom", updateScreenPoints);
    map.on("resize", updateScreenPoints);

    return () => {
      map.off("move", updateScreenPoints);
      map.off("zoom", updateScreenPoints);
      map.off("resize", updateScreenPoints);
    };
  }, [isMapLoaded, updateScreenPoints]);

  // Mouse Down handler for SVG Handles (Pan, Resize, Rotate)
  const handleHandleMouseDown = (e, type) => {
    if (isLocked) return;
    if (e.button !== 0) return; // Left click only for handles/overlay
    e.preventDefault();
    e.stopPropagation();

    const map = mapRef.current;
    if (!map) return;

    isDraggingActiveRef.current = true;
    map.dragPan.disable();
    if (map.boxZoom) map.boxZoom.disable();

    const startX = e.clientX;
    const startY = e.clientY;
    const startState = { ...visualState };
    const centerScreen = map.project(visualState.center);
    const initialAngle = Math.atan2(startY - centerScreen.y, startX - centerScreen.x);
    const initialDist = Math.sqrt(
      (startX - centerScreen.x) ** 2 + (startY - centerScreen.y) ** 2
    );

    const startCorners = [...corners];
    let lastX = startX;
    let lastY = startY;

    const onMouseMove = (moveEvent) => {
      const curX = moveEvent.clientX;
      const curY = moveEvent.clientY;

      if (type === "drag") {
        if (moveEvent.altKey || e.altKey) {
          // Alt + Drag anywhere inside polygon rotates around center
          const currentAngle = Math.atan2(curY - centerScreen.y, curX - centerScreen.x);
          const deltaAngle = currentAngle - initialAngle;
          setVisualState((prev) => ({
            ...prev,
            rotation: startState.rotation + deltaAngle,
          }));
        } else if (moveEvent.ctrlKey || e.ctrlKey || moveEvent.metaKey || e.metaKey) {
          // Ctrl / Cmd + Drag -> Pan the BASE MAP camera!
          const dx = curX - lastX;
          const dy = curY - lastY;
          lastX = curX;
          lastY = curY;
          if (map) {
            map.panBy([-dx, -dy], { animate: false });
          }
        } else {
          const startMouseLngLat = map.unproject([startX, startY]);
          const curMouseLngLat = map.unproject([curX, curY]);
          const deltaLng = curMouseLngLat.lng - startMouseLngLat.lng;
          const deltaLat = curMouseLngLat.lat - startMouseLngLat.lat;

          setVisualState((prev) => ({
            ...prev,
            center: [startState.center[0] + deltaLng, startState.center[1] + deltaLat],
          }));

          if (startCorners && startCorners.length === 4) {
            const updatedCorners = startCorners.map(([lng, lat]) => [
              lng + deltaLng,
              lat + deltaLat,
            ]);
            setCorners(updatedCorners);
            if (onCornersChangeRef.current) {
              isInternalUpdateRef.current = true;
              onCornersChangeRef.current(updatedCorners, visualState);
            }
          }
        }
      } else if (type === "resize") {
        const curDist = Math.sqrt(
          (curX - centerScreen.x) ** 2 + (curY - centerScreen.y) ** 2
        );
        const scaleFactor = curDist / (initialDist || 1);

        setVisualState((prev) => ({
          ...prev,
          scaleX: Math.max(0.05, Math.min(10.0, startState.scaleX * scaleFactor)),
          scaleY: Math.max(0.05, Math.min(10.0, startState.scaleY * scaleFactor)),
        }));
      } else if (type === "resize-w") {
        const cos = Math.cos(startState.rotation);
        const sin = Math.sin(startState.rotation);

        const startDx = startX - centerScreen.x;
        const startDy = startY - centerScreen.y;
        const initialLocalX = startDx * cos + startDy * sin;

        const curDx = curX - centerScreen.x;
        const curDy = curY - centerScreen.y;
        const curLocalX = curDx * cos + curDy * sin;

        const scaleFactorX = curLocalX / (initialLocalX || 1);

        setVisualState((prev) => ({
          ...prev,
          scaleX: Math.max(0.05, Math.min(10.0, startState.scaleX * scaleFactorX)),
        }));
      } else if (type === "resize-h") {
        const cos = Math.cos(startState.rotation);
        const sin = Math.sin(startState.rotation);

        const startDx = startX - centerScreen.x;
        const startDy = startY - centerScreen.y;
        const initialLocalY = -startDx * sin + startDy * cos;

        const curDx = curX - centerScreen.x;
        const curDy = curY - centerScreen.y;
        const curLocalY = -curDx * sin + curDy * cos;

        const scaleFactorY = curLocalY / (initialLocalY || 1);

        setVisualState((prev) => ({
          ...prev,
          scaleY: Math.max(0.05, Math.min(10.0, startState.scaleY * scaleFactorY)),
        }));
      } else if (type === "rotate") {
        const currentAngle = Math.atan2(curY - centerScreen.y, curX - centerScreen.x);
        const deltaAngle = currentAngle - initialAngle;

        setVisualState((prev) => ({
          ...prev,
          rotation: startState.rotation + deltaAngle,
        }));
      }
    };

    const onMouseUp = () => {
      isDraggingActiveRef.current = false;
      if (mapRef.current) {
        mapRef.current.dragPan.enable();
      }
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  // Reset overlay to current map center
  const handleFitToView = () => {
    const map = mapRef.current;
    if (!map || !aspectRatio) return;

    const center = map.getCenter();
    const zoom = map.getZoom();

    const baseMercSize = (1 / Math.pow(2, zoom)) * 0.35;
    const newCenter = [center.lng, center.lat];

    setBaseMercWidth(baseMercSize);
    setBaseMercHeight(baseMercSize / aspectRatio);
    setVisualState({ center: newCenter, scaleX: 1.0, scaleY: 1.0, rotation: 0.0 });
  };


  // Rotation Step Handlers
  const handleRotateStep = (deltaDeg) => {
    if (isLocked) return;
    const deltaRad = (deltaDeg * Math.PI) / 180;
    setVisualState((prev) => ({
      ...prev,
      rotation: prev.rotation + deltaRad,
    }));
  };

  const handleResetRotation = () => {
    if (isLocked) return;
    setVisualState((prev) => ({
      ...prev,
      rotation: 0.0,
    }));
  };

  // Image Overlay Zoom Handlers
  const zoomIntervalRef = useRef(null);

  const startContinuousOverlayZoom = (factor) => {
    stopContinuousOverlayZoom();
    if (isLocked) return;
    setVisualState((prev) => ({
      ...prev,
      scaleX: Math.min(10.0, Math.max(0.05, parseFloat((prev.scaleX * factor).toFixed(4)))),
      scaleY: Math.min(10.0, Math.max(0.05, parseFloat((prev.scaleY * factor).toFixed(4)))),
    }));

    const stepFactor = factor > 1 ? 1.001 : 0.999;
    zoomIntervalRef.current = setInterval(() => {
      setVisualState((prev) => ({
        ...prev,
        scaleX: Math.min(10.0, Math.max(0.05, parseFloat((prev.scaleX * stepFactor).toFixed(4)))),
        scaleY: Math.min(10.0, Math.max(0.05, parseFloat((prev.scaleY * stepFactor).toFixed(4)))),
      }));
    }, 40);
  };

  const stopContinuousOverlayZoom = () => {
    if (zoomIntervalRef.current) {
      clearInterval(zoomIntervalRef.current);
      zoomIntervalRef.current = null;
    }
  };

  const handleResetImageZoom = () => {
    if (isLocked) return;
    stopContinuousOverlayZoom();
    setVisualState((prev) => ({
      ...prev,
      scaleX: 1.0,
      scaleY: 1.0,
    }));
  };

  // Wheel scroll event handler for map canvas and SVG overlay
  const handleSvgWheel = (e) => {
    const map = mapRef.current;
    if (!map) return;

    // Shift + Wheel resizes image overlay scale when unlocked
    if (e.shiftKey && !isLocked) {
      e.preventDefault();
      const scaleFactor = e.deltaY < 0 ? 1.001 : 0.999;
      setVisualState((prev) => ({
        ...prev,
        scaleX: Math.max(0.05, Math.min(10.0, parseFloat((prev.scaleX * scaleFactor).toFixed(4)))),
        scaleY: Math.max(0.05, Math.min(10.0, parseFloat((prev.scaleY * scaleFactor).toFixed(4)))),
      }));
      return;
    }

    // Default: forward wheel event directly to map canvas for native smooth MapLibre GL scrollZoom
    const canvas = map.getCanvas();
    if (canvas) {
      const native = e.nativeEvent;
      const wheelInit = {
        clientX: native.clientX,
        clientY: native.clientY,
        screenX: native.screenX,
        screenY: native.screenY,
        deltaX: native.deltaX,
        deltaY: native.deltaY,
        deltaZ: native.deltaZ,
        deltaMode: native.deltaMode,
        ctrlKey: native.ctrlKey,
        shiftKey: native.shiftKey,
        altKey: native.altKey,
        metaKey: native.metaKey,
        bubbles: true,
        cancelable: true,
      };
      canvas.dispatchEvent(new WheelEvent("wheel", wheelInit));
    }
  };

  // Render SVG interactive handles overlay
  let overlaySvg = null;
  if (screenPoints.length === 4 && !isLocked) {
    const [tl, tr, br, bl] = screenPoints;
    const tc = { x: (tl.x + tr.x) / 2, y: (tl.y + tr.y) / 2 };

    const dx = tr.x - tl.x;
    const dy = tr.y - tl.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const ux = -dy / len;
    const uy = dx / len;
    const rot = { x: tc.x + ux * 35, y: tc.y + uy * 35 };
    const centerPt = { x: (tl.x + br.x) / 2, y: (tl.y + br.y) / 2 };

    // Edge midpoints
    const tMid = { x: (tl.x + tr.x) / 2, y: (tl.y + tr.y) / 2 };
    const rMid = { x: (tr.x + br.x) / 2, y: (tr.y + br.y) / 2 };
    const bMid = { x: (br.x + bl.x) / 2, y: (br.y + bl.y) / 2 };
    const lMid = { x: (bl.x + tl.x) / 2, y: (bl.y + tl.y) / 2 };

    overlaySvg = (
      <svg
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{ pointerEvents: "none", zIndex: 10, select: "none" }}
        onWheel={handleSvgWheel}
      >
        {/* Polygon Border for Panning */}
        <polygon
          points={`${tl.x},${tl.y} ${tr.x},${tr.y} ${br.x},${br.y} ${bl.x},${bl.y}`}
          fill={dragTargetMode === "map" || isCtrlPressed ? "rgba(99, 102, 241, 0.05)" : "rgba(99, 102, 241, 0.12)"}
          stroke="#6366f1"
          strokeWidth="2.5"
          style={{
            pointerEvents: "fill",
            cursor: dragTargetMode === "map" || isCtrlPressed ? "grab" : "move"
          }}
          onMouseDown={(e) => handleHandleMouseDown(e, "drag")}
        />

        {/* Stem Line to Rotation Handle */}
        <line
          x1={tc.x}
          y1={tc.y}
          x2={rot.x}
          y2={rot.y}
          stroke="#6366f1"
          strokeWidth="2"
          strokeDasharray="3,3"
        />

        {/* Corner Resize Handles */}
        {[
          { pt: tl, cursor: getRotatedCursor("nwse", visualState.rotation) },
          { pt: tr, cursor: getRotatedCursor("nesw", visualState.rotation) },
          { pt: br, cursor: getRotatedCursor("nwse", visualState.rotation) },
          { pt: bl, cursor: getRotatedCursor("nesw", visualState.rotation) },
        ].map((h, i) => (
          <rect
            key={i}
            x={h.pt.x - 6}
            y={h.pt.y - 6}
            width={12}
            height={12}
            fill="#ffffff"
            stroke="#6366f1"
            strokeWidth="2.5"
            rx={2}
            style={{ pointerEvents: "auto", cursor: h.cursor }}
            onMouseDown={(e) => handleHandleMouseDown(e, "resize")}
          />
        ))}

        {/* Edge Resize Handles */}
        {[
          { pt: tMid, type: "resize-h", cursor: getRotatedCursor("h", visualState.rotation) },
          { pt: rMid, type: "resize-w", cursor: getRotatedCursor("w", visualState.rotation) },
          { pt: bMid, type: "resize-h", cursor: getRotatedCursor("h", visualState.rotation) },
          { pt: lMid, type: "resize-w", cursor: getRotatedCursor("w", visualState.rotation) },
        ].map((h, i) => (
          <circle
            key={`edge-${i}`}
            cx={h.pt.x}
            cy={h.pt.y}
            r={5.5}
            fill="#ffffff"
            stroke="#6366f1"
            strokeWidth="2.5"
            style={{ pointerEvents: "auto", cursor: h.cursor }}
            onMouseDown={(e) => handleHandleMouseDown(e, h.type)}
          />
        ))}

        {/* Rotation Handle */}
        <circle
          cx={rot.x}
          cy={rot.y}
          r={7}
          fill="#ffffff"
          stroke="#6366f1"
          strokeWidth="2.5"
          style={{ pointerEvents: "auto", cursor: "grab" }}
          onMouseDown={(e) => handleHandleMouseDown(e, "rotate")}
        />

        {/* Center Move Handle Pin */}
        <circle
          cx={centerPt.x}
          cy={centerPt.y}
          r={8}
          fill="#6366f1"
          stroke="#ffffff"
          strokeWidth="2"
          style={{ pointerEvents: "auto", cursor: "move" }}
          onMouseDown={(e) => handleHandleMouseDown(e, "drag")}
        />
      </svg>
    );
  }

  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDraggingFile(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && onFileUpload) {
      onFileUpload({ target: { files: [file] } });
    }
  };

  // Location Search State
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const handleLocationSearch = async (e) => {
    if (e) e.preventDefault();
    const query = searchQuery.trim();
    if (!query || !mapRef.current) return;

    // 1. Check if user entered lat/lng coordinates (e.g. "21.1702, 72.8311" or "21.1702 72.8311")
    const coordMatch = query.match(/^(-?\d+(?:\.\d+)?)\s*[\s,]\s*(-?\d+(?:\.\d+)?)$/);
    if (coordMatch) {
      const lat = parseFloat(coordMatch[1]);
      const lng = parseFloat(coordMatch[2]);
      if (!isNaN(lat) && !isNaN(lng)) {
        mapRef.current.flyTo({ center: [lng, lat], zoom: 15 });
        return;
      }
    }

    // 2. Query Nominatim Geocoding API for place names (Surat, Ahmedabad, etc.)
    try {
      setIsSearching(true);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`
      );
      const results = await response.json();
      if (results && results.length > 0) {
        const place = results[0];
        const lng = parseFloat(place.lon);
        const lat = parseFloat(place.lat);
        mapRef.current.flyTo({ center: [lng, lat], zoom: 14 });
      } else {
        toast.error("No location found for: " + query);
      }
    } catch (err) {
      console.error("Location search failed:", err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  return (
    <div
      className="w-100 h-100 position-relative overflow-hidden bg-dark rounded-3"
      style={{ height: "100%", minHeight: "680px" }}
    >
      {/* Map Control Bar Overlay */}
      <div
        className="position-absolute top-0 start-0 m-3 p-2 bg-white rounded-3 shadow-sm d-flex align-items-center gap-3"
        style={{ zIndex: 20, minWidth: "320px" }}
      >
        <Button
          size="sm"
          variant={isLocked ? "warning" : "outline-secondary"}
          className="d-flex align-items-center gap-1.5 py-1 px-2 fs-xs"
          onClick={() => setIsLocked(!isLocked)}
        >
          {isLocked ? <Lock size={14} /> : <Unlock size={14} />}
          {/* {isLocked ? "Locked" : "Lock Position"} */}
        </Button>

        {/* Location Search Bar */}
        <Form onSubmit={handleLocationSearch} className="d-flex align-items-center me-1">
          <div className="position-relative d-flex align-items-center">
            <Form.Control
              type="text"
              size="sm"
              placeholder="Search place or lat, lng..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pe-4 fs-xs"
              style={{ width: "195px", height: "30px", fontSize: "11px" }}
            />
            {searchQuery ? (
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={handleClearSearch}
                className="position-absolute end-0 p-1 text-secondary border-0"
              >
                <X size={13} />
              </Button>
            ) : (
              <Button
                type="submit"
                variant="link"
                size="sm"
                disabled={isSearching}
                className="position-absolute end-0 p-1 text-secondary border-0"
              >
                {isSearching ? <div className="spinner-border spinner-border-sm" role="status" style={{ width: "12px", height: "12px" }} /> : <Search size={13} />}
              </Button>
            )}
          </div>
        </Form>


        {/* Image Scale Controls */}
        <div className="d-flex align-items-center bg-light border rounded-2 p-0.5" title="Shift + Mouse Wheel or Hold buttons to micro scale image">
          <Button
            size="sm"
            variant="light"
            disabled={isLocked}
            className="py-0.5 px-1.5 fs-xs d-flex align-items-center text-dark"
            title="Zoom Image Out (Hold to glide)"
            onMouseDown={() => startContinuousOverlayZoom(0.999)}
            onMouseUp={stopContinuousOverlayZoom}
            onMouseLeave={stopContinuousOverlayZoom}
          >
            <ZoomOut size={13} />
          </Button>
          <Badge
            bg={Math.abs(visualState.scaleX - 1.0) > 0.001 ? "primary" : "secondary"}
            className="fs-xs px-2"
            style={{ cursor: isLocked ? "default" : "pointer" }}
            title="Click to reset Image Scale to 100%"
            onClick={handleResetImageZoom}
          >
            Image {(visualState.scaleX * 100).toFixed(1)}%
          </Badge>
          <Button
            size="sm"
            variant="light"
            disabled={isLocked}
            className="py-0.5 px-1.5 fs-xs d-flex align-items-center text-dark"
            title="Zoom Image In (Hold to glide)"
            onMouseDown={() => startContinuousOverlayZoom(1.001)}
            onMouseUp={stopContinuousOverlayZoom}
            onMouseLeave={stopContinuousOverlayZoom}
          >
            <ZoomIn size={13} />
          </Button>
        </div>

        {/* Image Rotation Controls */}
        <div className="d-flex align-items-center bg-light border rounded-2 p-0.5" title="Hold Alt + Drag on image overlay to rotate">
          <Button
            size="sm"
            variant="light"
            disabled={isLocked}
            className="py-0.5 px-1.5 fs-xs d-flex align-items-center text-dark"
            title="Rotate 90° Counter-Clockwise"
            onClick={() => handleRotateStep(-90)}
          >
            <RotateCcw size={13} />
          </Button>
          <Button
            size="sm"
            variant="light"
            disabled={isLocked}
            className="py-0.5 px-1 fs-xs text-dark"
            title="Fine-tune Rotate -1°"
            onClick={() => handleRotateStep(-1)}
          >
            -1°
          </Button>
          <Badge
            bg={Math.abs(visualState.rotation) > 0.001 ? "primary" : "secondary"}
            className="fs-xs px-2"
            style={{ cursor: isLocked ? "default" : "pointer" }}
            title="Click to reset Rotation Angle to 0°"
            onClick={handleResetRotation}
          >
            Rotate {Math.round((((visualState.rotation * 180) / Math.PI) % 360 + 360) % 360)}°
          </Badge>
          <Button
            size="sm"
            variant="light"
            disabled={isLocked}
            className="py-0.5 px-1 fs-xs text-dark"
            title="Fine-tune Rotate +1°"
            onClick={() => handleRotateStep(1)}
          >
            +1°
          </Button>
          <Button
            size="sm"
            variant="light"
            disabled={isLocked}
            className="py-0.5 px-1.5 fs-xs d-flex align-items-center text-dark"
            title="Rotate 90° Clockwise"
            onClick={() => handleRotateStep(90)}
          >
            <RotateCw size={13} />
          </Button>
        </div>

        <Button
          size="sm"
          variant="outline-secondary"
          className="d-flex align-items-center gap-1 py-1 px-2 fs-xs"
          onClick={handleFitToView}
        >
          <Compass size={14} />
          {/* Center Here */}
        </Button>

        {/* Basemap Style Switcher */}
        <div className="d-flex align-items-center bg-light border rounded-2 p-0.5">
          <Button
            size="sm"
            variant={mapStyle === "streets" ? "primary" : "light"}
            className="py-0.5 px-2 fs-xs d-flex align-items-center gap-1"
            onClick={() => setMapStyle("streets")}
          >
            <Layers size={13} />
            {/* Streets */}
          </Button>
          <Button
            size="sm"
            variant={mapStyle === "satellite" ? "primary" : "light"}
            className="py-0.5 px-2 fs-xs d-flex align-items-center gap-1"
            onClick={() => setMapStyle("satellite")}
          >
            <Globe size={13} />
            {/* Satellite */}
          </Button>
        </div>

        {/* Nearby Completed Sheets Toggle */}
        <Button
          size="sm"
          variant={showNearbyCompleted ? "primary" : "outline-secondary"}
          className="d-flex align-items-center gap-1.5 py-1 px-2 fs-xs"
          onClick={() => setShowNearbyCompleted(!showNearbyCompleted)}
          title="Toggle visibility of nearby completed map sheets"
        >
          <MapIcon size={14} />
          {showNearbyCompleted ? "Hide Nearby" : "Show Nearby"}
        </Button>

        {/* Map Type Filter */}
        {showNearbyCompleted && (
          <div style={{ minWidth: "155px" }}>
            <CustomSelect
              placeholder="All Map Types"
              options={mapTypes.map(t => ({ value: t.id, label: t.name }))}
              value={selectedMapTypes}
              isMulti={true}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              selectProps={{
                components: { Option: CheckboxOption, MultiValue: MultiValueMessage },
                styles: {
                  menu: (provided) => ({ ...provided, minWidth: "220px", zIndex: 9999 }),
                  multiValueRemove: () => ({ display: "none" }),
                  option: (provided, state) => ({
                    ...provided,
                    fontSize: "12px",
                    backgroundColor: state.isSelected
                      ? "transparent"
                      : state.isFocused
                        ? "var(--primary-100)"
                        : "transparent",
                    color: "var(--text-primary)",
                    ":active": { backgroundColor: "var(--primary-100)" }
                  })
                }
              }}
              onChange={(selected) => setSelectedMapTypes(selected || [])}
              isClearable={true}
              label={{ isHidden: true }}
              className="mb-0"
            />
          </div>
        )}

        <Button
          size="sm"
          variant="outline-dark"
          className="d-flex align-items-center gap-1 py-1 px-2 fs-xs"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload size={14} />
          {imageUrl ? "Replace Map" : "Upload Map"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onFileUpload}
          className="d-none"
        />

        {/* Opacity Control */}
        <div className="d-flex align-items-center gap-2 flex-grow-1 ms-2">
          <span className="text-muted fs-xs fw-semibold">Opacity:</span>
          <Form.Range
            min={0.1}
            max={1.0}
            step={0.05}
            value={opacity}
            onChange={(e) => setOpacity(parseFloat(e.target.value))}
            style={{ width: "90px" }}
          />
          <Badge bg="secondary" className="fs-xs">
            {Math.round(opacity * 100)}%
          </Badge>
        </div>
      </div>

      {/* MapLibre Container */}
      <div
        ref={mapContainerRef}
        className="w-100 h-100 position-absolute top-0 start-0"
        style={{ height: "100%", minHeight: "680px" }}
      />

      {/* SVG Drag & Place Handle Overlay */}
      {overlaySvg}

      {/* Upload Bordered Image Dropzone Overlay when no image is loaded */}
      {!imageUrl && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDraggingFile(true);
          }}
          onDragLeave={() => setIsDraggingFile(false)}
          onDrop={handleFileDrop}
          className={`position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-center p-4 transition-all ${isDraggingFile ? "border border-2 border-primary" : ""
            }`}
          style={{
            zIndex: 25,
            backgroundColor: isDraggingFile ? "rgba(15, 23, 42, 0.98)" : "rgba(15, 23, 42, 0.94)",
            backdropFilter: "blur(6px)",
          }}
        >
          <Upload size={52} className="text-primary mb-3 animate-bounce" />
          <h5 className="fw-semibold text-white mb-2">Upload Bordered Map Image Sheet</h5>
          <p className="text-slate-300 fs-xs mb-4" style={{ maxWidth: "440px" }}>
            A bordered map image is required to georeference this sheet. Please upload or drag and drop a clean bordered image file (PNG/JPG) below.
          </p>
          <Button
            variant="primary"
            size="sm"
            className="d-flex align-items-center gap-2 px-3 py-2 fs-xs"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={15} />
            Browse & Upload Bordered Image
          </Button>
        </div>
      )}
    </div>
  );
}
